<script lang="ts" setup>
  import type { ButtonProps } from 'naive-ui'

  import dayjs from 'dayjs'

  import { useScreenSize } from '@/shared/lib'
  import { useAppConfig, useComponentConfig, useTenant } from '@/shared/lib/tenant'

  import { useProfile } from '@/entities/profile'

  import { LanguageSwitcher } from '@/features/locale'

  definePage({
    meta: {
      layout: 'auth',
    },
  })

  const { t } = useI18n()
  useHead({
    title: computed(() => t('page_title_sign_in')),
  })

  const route = useRoute()
  const router = useRouter()
  const profileStore = useProfile()
  const { profile } = storeToRefs(profileStore)
  const { initProfile } = profileStore

  const showNoAccess = computed(() => route.query.no_access === 'true')

  async function checkUser() {
    await initProfile()
    if (profile.value) {
      router.push('/')
    }
  }

  checkUser()

  const currentYear = dayjs().year()
  const { isMobile } = storeToRefs(useScreenSize())

  const origin = window.location.origin
  const signInHref = `${origin}/auth-service/login?return_to=${origin}/auth/sign-in-status`

  const { componentsConfig } = useComponentConfig()
  const { signInPageLogoUrl } = useAppConfig()
  const { tenantName } = useTenant()

  const signInButtonThemeOverrides: NonNullable<ButtonProps['themeOverrides']> = {
    heightMedium: 'calc(var(--spacing) * 10)',
    borderRadiusMedium: 'var(--radius-full)',
    paddingMedium: '0 calc(var(--spacing) * 3.5)',
    fontSizeMedium: 'var(--text-sm)',
  }

  const bgImage = computed(() => componentsConfig.value?.auth?.logoContainerBackground)

  function goBackToSignIn() {
    router.replace('/auth/sign-in')
  }
</script>

<template>
  <div
    class="mobile:grid max-mobile:flex max-mobile:flex-col max-mobile:px-4 max-mobile:py-0 max-h-dvh w-full grid-cols-2 overflow-hidden"
  >
    <!-- TOP/LEFT SIDE -->
    <div
      v-if="isMobile"
      class="h-full w-full shrink-0 basis-[55%]"
    >
      <NImage
        :src="bgImage"
        class="h-full max-h-[750px] w-full translate-x-[-20%] translate-y-[-10%]"
        object-fit="cover"
        alt=""
      />

      <div class="absolute top-4 right-6">
        <LanguageSwitcher
          direction="down"
          variant="auth"
        />
      </div>
    </div>
    <div
      v-else
      class="relative flex h-full max-h-dvh w-full items-center justify-center bg-(--color-primary)"
    >
      <NImage
        :src="bgImage"
        object-fit="scale-down"
        class="absolute max-h-[700px]! w-full max-w-[700px]!"
        alt=""
      />

      <NImage
        :src="signInPageLogoUrl"
        class="absolute bottom-8 z-1 flex w-full max-w-[340px] items-center justify-center"
        alt=""
      />
    </div>

    <!-- BOTTOM/RIGHT SIDE -->
    <div
      class="max-mobile:basis-[45%] max-mobile:items-start! mobile:items-center! relative flex h-full justify-center"
    >
      <div
        v-if="showNoAccess"
        class="max-mobile:gap-4 flex w-full max-w-[389px] flex-col gap-6"
      >
        <div class="flex flex-col gap-2">
          <h1 class="text-3xl leading-9 font-medium tracking-tight text-(--color-body)">
            {{ $t('auth_no_access_title') }}
          </h1>

          <p class="text-sm leading-[22px] text-(--color-subtitle)">
            {{ $t('auth_no_access_description') }}
          </p>
        </div>

        <NButton
          type="primary"
          size="medium"
          :bordered="false"
          :theme-overrides="signInButtonThemeOverrides"
          class="w-fit! cursor-pointer! opacity-100! outline-none! *:w-fit! *:cursor-pointer! *:opacity-100! *:outline-none! [&_.n-base-wave]:hidden"
          @click="goBackToSignIn"
        >
          {{ $t('auth_back') }}
        </NButton>
      </div>
      <div
        v-else
        class="max-mobile:gap-4 flex w-full max-w-[389px] flex-col gap-6"
      >
        <div class="flex flex-col gap-2">
          <h1
            class="max-mobile:text-xl! max-mobile:leading-[28px]! max-mobile:-tracking-[-0.2px] text-3xl! leading-9! font-medium! tracking-tight! text-(--color-body)"
          >
            {{ $t('auth_title_welcome_to') }}
            <br />
            <span>{{ $t('auth_title_corp') }}</span>
          </h1>

          <p class="max-mobile:text-[13px] max-mobile:leading-5 text-sm leading-[22px] text-(--color-subtitle)">
            {{ $t('auth_description') }}
          </p>
        </div>

        <NButton
          tag="a"
          :href="signInHref"
          type="primary"
          size="medium"
          :bordered="false"
          :theme-overrides="signInButtonThemeOverrides"
          class="max-mobile:w-full! w-fit! cursor-pointer! opacity-100! outline-none! *:w-fit! *:cursor-pointer! *:opacity-100! *:outline-none! [&_.n-base-wave]:hidden"
        >
          {{ $t('auth_login') }}
        </NButton>
      </div>

      <footer
        class="max-mobile:relative max-mobile:mt-auto max-mobile:bottom-6 mobile:absolute mobile:bottom-8 flex w-full flex-col items-center gap-5"
      >
        <LanguageSwitcher
          v-if="!isMobile"
          direction="up"
          variant="auth"
        />
        <RouterLink
          to="/privacy-policy"
          class="text-[11px] leading-4 text-(--color-subtitle) hover:underline"
        >
          {{ $t('auth_privacy_policy') }}
        </RouterLink>
        <div class="text-xs leading-3 text-(--color-subtitle)">© {{ currentYear }} {{ tenantName }}</div>
      </footer>
    </div>
  </div>
</template>
